import { FC, ReactNode, useMemo } from 'react';

import { DateTime } from 'luxon';

import { useQuery } from '@apollo/client';
import { IconLock } from '@tabler/icons-react';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { formatCurrency } from 'configs/columns';

import { SALE_POOL_VALUATIONS_QUERY } from 'query/sales';
import { SalePoolValuations } from 'query/__generated__/sales';

type StoredValuation = SalePoolValuations['salePoolValuations'][number];

export type SalePoolValuationsState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'loaded'; byOfferingId: Map<string, StoredValuation> };

export const VALUATION_GROUP_LABEL = 'FDIC valuation · seller only';
export const NOT_VALUED = 'Not valued';
export const VALUATIONS_FAILED = "Couldn't load valuations";
export const VALUATION_LOADING_TEST_ID = 'sale-pool-valuation-loading';

const METHOD_LABEL: Record<string, string> = { model: 'Model', proxy: 'Recovery proxy' };

// The calculation date is a date, stored as midnight UTC: read in a local zone west of UTC it
// would show the day before.
const formatCalculationDate = (seconds: number): string =>
  DateTime.fromSeconds(Number(seconds), { zone: 'utc' }).toFormat('M/d/yyyy');

const formatPctOfBookValue = (pct: number | null): string =>
  pct == null ? '-' : `${pct.toFixed(2)}%`;

// An inset shadow tints over whatever background the table paints the cell (striped rows, row
// hover, the light theme) where a background class would replace it.
const VALUATION_TINT =
  'shadow-[inset_0_0_0_100vmax_rgb(255_207_116/0.09)] light:shadow-[inset_0_0_0_100vmax_rgb(255_207_116/0.28)]';
const VALUATION_GROUP_EDGE = 'border-l-2 border-l-yellow-500 light:border-l-yellow-600';

const GROUP_EDGE_CLASSES = `${VALUATION_TINT} ${VALUATION_GROUP_EDGE}`;

// The FDIC's own market-value estimates: the api answers this only for the sale's seller, and
// only the seller's Pools table mounts it, so a bidder's browser never asks.
export const useSalePoolValuations = (saleId: string): SalePoolValuationsState => {
  const { data, loading, error } = useQuery<SalePoolValuations>(SALE_POOL_VALUATIONS_QUERY, {
    variables: { saleId },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const valuations = data?.salePoolValuations;
  const isLoading = loading && !data;
  const failed = !!error && !valuations;

  return useMemo(() => {
    if (isLoading) {
      return { status: 'loading' };
    }
    if (failed) {
      return { status: 'failed' };
    }
    return {
      status: 'loaded',
      byOfferingId: new Map(
        (valuations ?? []).map((valuation) => [valuation.offering_id, valuation]),
      ),
    };
  }, [isLoading, failed, valuations]);
};

const ValuationGroupHeader: FC<{ valuations: SalePoolValuationsState }> = ({ valuations }) => (
  <div className="flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-yellow-300 light:text-yellow-700">
    <IconLock size={12} aria-hidden />
    <span>{VALUATION_GROUP_LABEL}</span>
    {valuations.status === 'loading' && <span className="sr-only">Loading valuations</span>}
    {valuations.status === 'failed' && (
      <>
        <span aria-hidden>·</span>
        <span className="font-normal text-red-300 light:text-error-muted">{VALUATIONS_FAILED}</span>
      </>
    )}
  </div>
);

const LoadingPlaceholder = () => (
  <span
    data-testid={VALUATION_LOADING_TEST_ID}
    aria-hidden
    className="inline-block h-3 w-16 animate-pulse rounded bg-gray-800 light:bg-gray-300"
  />
);

type PoolRow = { offering_id: string };

// A failed query leaves the cells empty: the group header says so once.
const valuationCell =
  <Row extends PoolRow>(
    valuations: SalePoolValuationsState,
    render: (valuation: StoredValuation | undefined) => ReactNode,
  ) =>
  (props: CellContext<Row, unknown>) => {
    if (valuations.status === 'loading') {
      return <LoadingPlaceholder />;
    }
    if (valuations.status === 'failed') {
      return null;
    }
    return render(valuations.byOfferingId.get(props.row.original.offering_id));
  };

// salePoolValuations leaves out a pool with no valuation, so the rows follow the sale's pools and
// a missing one says so.
export const salePoolValuationColumnGroup = <Row extends PoolRow>(
  valuations: SalePoolValuationsState,
): ColumnDef<Row, any> => ({
  id: 'fdic_valuation',
  header: () => <ValuationGroupHeader valuations={valuations} />,
  meta: {
    numeric: false,
    alignment: 'left',
    headerNoPill: true,
    headerClassName: GROUP_EDGE_CLASSES,
  },
  columns: [
    {
      id: 'value',
      header: 'Value',
      cell: valuationCell<Row>(valuations, (valuation) =>
        valuation ? (
          <span>{formatCurrency(valuation.value_cents)}</span>
        ) : (
          <span className="text-gray-400 light:text-gray-700">{NOT_VALUED}</span>
        ),
      ),
      meta: {
        numeric: true,
        alignment: 'left',
        headerClassName: GROUP_EDGE_CLASSES,
        cellClassName: GROUP_EDGE_CLASSES,
      },
    },
    {
      id: 'pct_of_book_value',
      header: '% of Book Value',
      cell: valuationCell<Row>(valuations, (valuation) => (
        <span>{valuation ? formatPctOfBookValue(valuation.pct_of_book_value) : '-'}</span>
      )),
      meta: {
        numeric: true,
        alignment: 'left',
        headerClassName: VALUATION_TINT,
        cellClassName: VALUATION_TINT,
      },
    },
    {
      id: 'calculation_date',
      header: 'Calculation Date',
      cell: valuationCell<Row>(valuations, (valuation) => (
        <span>{valuation ? formatCalculationDate(valuation.calculation_date_seconds) : '-'}</span>
      )),
      meta: {
        numeric: false,
        alignment: 'left',
        headerClassName: VALUATION_TINT,
        cellClassName: VALUATION_TINT,
      },
    },
    {
      id: 'method',
      header: 'Method',
      cell: valuationCell<Row>(valuations, (valuation) => {
        const method = valuation?.method;
        return <span>{method ? (METHOD_LABEL[method] ?? method) : '-'}</span>;
      }),
      meta: {
        numeric: false,
        alignment: 'left',
        headerClassName: VALUATION_TINT,
        cellClassName: VALUATION_TINT,
      },
    },
  ],
});
